import { create } from 'zustand';
import { CustomProjectEntity } from '@entities/CustomProject/Types';
import {
    emptyPagination,
    PaginationResponse,
} from '@shared/Interfaces/Api/Pagination';
import { CustomProjectModel } from '@entities/CustomProject/CustomProjectModel';
import { CustomProjectsGateway } from '../../Processes/Gateways/CustomProjects/CustomProjectsGateway';
import { ApiResult } from '@shared/Interfaces/Api/ApiResult';

const projectGateway = new CustomProjectsGateway();

export interface CustomProjectsList {
    data: CustomProjectEntity[];
    pagination: PaginationResponse;
}

const EmptyCustomProjectList: CustomProjectsList = {
    data: [],
    pagination: emptyPagination,
};

interface State {
    customProjects: CustomProjectsList;
    error: string | null;
    currentPage: number;

    isProjectsListLoading: boolean;

    setCurrentPage: (page: number) => void;
    fetchCustomProjectsList: (page?: number) => Promise<void>;
    deleteCustomProject: (projectId: number) => Promise<ApiResult<boolean>>;
}

export const useProjectsListViewModel = create<State>((set, get) => ({
    customProjects: EmptyCustomProjectList,
    error: null,
    currentPage: 1,

    isProjectsListLoading: false,

    setCurrentPage: (page: number) => {
        set({ currentPage: page });
    },

    fetchCustomProjectsList: async (page?: number) => {
        set({ isProjectsListLoading: true, currentPage: page, error: null });

        const response = await projectGateway.getCustomProjectsList(page);

        if (response.error || !response.data) {
            set({
                isProjectsListLoading: false,
                error: response.message || 'An unknown error occurred',
            });
            return;
        }

        const projectData = Array.isArray(response.data.data)
            ? response.data.data.map(
                  rawProject => new CustomProjectModel(rawProject)
              )
            : [new CustomProjectModel(response.data.data)];

        const projects: CustomProjectsList = {
            data: projectData,
            pagination: response.data.pagination,
        };

        set({
            isProjectsListLoading: false,
            customProjects: projects,
            error: null,
        });
    },

    deleteCustomProject: async (
        projectId: number
    ): Promise<ApiResult<boolean>> => {
        const response =
            await projectGateway.deleteCustomProjectById(projectId);

        if (response.error) {
            return {
                error: response.error,
                message: 'Failed to delete project',
            };
        }

        return { error: false, message: 'Project deleted.' };
    },
}));
